import { PERMISSIONS, type PermissionCode } from '@/lib/permissions'

/**
 * The ledger's screens, and how to register them.
 *
 * The four names the router already imports — `LedgerPage`, `LedgerBusinessDaysPage`,
 * `LedgerJournalsPage`, `LedgerProvisioningPage` — keep their names and their paths, so nothing in
 * `router.tsx` has to change for the module to keep working. The seven new ones sit alongside them
 * and registering each is additive: every path below is static, with no parameters, so a route can
 * be added with `createRoute({ getParentRoute: () => appRoute, path, component })` and nothing else.
 *
 * That every path is parameterless is a decision rather than a coincidence. A journal detail and an
 * account detail both want an identifier in the URL, and until the integrator registers such a
 * route a typed `<Link>` to it would not compile — so those two screens are master-and-detail on one
 * route instead. It is also, as it happens, the shape a reconciliation and a chart walk are actually
 * worked in.
 *
 * `LedgerPage` carries every section as a tab as well, so the module is complete whether or not the
 * new routes are registered.
 */
export { LedgerPage } from './ledger-page'
export { LedgerBusinessDaysPage } from './business-days'
export { LedgerProvisioningPage } from './provisioning'
export { LedgerJournalsPage } from './journals'
export { LedgerManualJournalPage } from './manual-journal'
export { LedgerChartOfAccountsPage } from './chart-of-accounts'
export { LedgerTrialBalancePage } from './trial-balance'
export { LedgerDailyBalancesPage } from './daily-balances'
export { LedgerPostingPeriodsPage } from './posting-periods'
export { LedgerBalanceDriftPage } from './balance-drift'
export { LedgerAuditPage } from './audit'

/**
 * How a ledger screen behaves below the `lg` breakpoint — `docs/mobile-policy.md`.
 *
 * `reads` means the screen adapts: tables become cards, nothing is lost. `refuses` means it renders
 * `RequiresWiderScreen` instead of a squeezed version of itself, because doing this work on a phone
 * would be error-prone in a way that costs real money. `mixed` means the split is made per section
 * inside the screen — the read adapts and the write refuses.
 */
export type LedgerPhoneBehaviour = 'reads' | 'refuses' | 'mixed'

export interface LedgerRouteDefinition {
  path: string
  componentName: string
  /**
   * The code the route's own gate uses. Route guards are a courtesy to the operator rather than a
   * security boundary — the server authorises every request against the live catalogue — but they
   * matter here for a specific reason: every one of these paths is directly addressable, so hiding a
   * navigation item gates nothing and each screen carries its own gate.
   */
  permission: PermissionCode
  phoneBehaviour: LedgerPhoneBehaviour
}

export const ledgerRoutes: LedgerRouteDefinition[] = [
  {
    // Already registered. The module's landing page: every section as a tab, plus what the ledger
    // still cannot do. Gated in the page on holding any ledger permission at all; this code is the
    // one the navigation item already uses.
    path: '/ledger',
    componentName: 'LedgerPage',
    permission: PERMISSIONS.ledger.journalView,
    phoneBehaviour: 'mixed',
  },
  {
    // Already registered. Reading the position needs no permission — the business-day endpoint
    // deliberately carries none, because every screen in the institution stamps its work with that
    // date. Opening and closing are what the gates are for, and both are inside the screen.
    path: '/ledger/business-days',
    componentName: 'LedgerBusinessDaysPage',
    permission: PERMISSIONS.ledger.dayOpen,
    phoneBehaviour: 'mixed',
  },
  {
    // Already registered.
    path: '/ledger/provisioning',
    componentName: 'LedgerProvisioningPage',
    permission: PERMISSIONS.ledger.provisionRun,
    phoneBehaviour: 'refuses',
  },
  {
    // Already registered. List and detail on one route: the entry is opened beneath the list rather
    // than at a path of its own, so no parameterised route is needed.
    path: '/ledger/journals',
    componentName: 'LedgerJournalsPage',
    permission: PERMISSIONS.ledger.journalView,
    phoneBehaviour: 'reads',
  },
  {
    // New. Money is keyed here and the entry has to be seen whole, so the screen refuses below `lg`.
    path: '/ledger/journals/new',
    componentName: 'LedgerManualJournalPage',
    permission: PERMISSIONS.ledger.journalManual,
    phoneBehaviour: 'refuses',
  },
  {
    // New. Tree and account detail on one route, for the same reason the journals screen is.
    path: '/ledger/accounts',
    componentName: 'LedgerChartOfAccountsPage',
    permission: PERMISSIONS.ledger.accountView,
    phoneBehaviour: 'reads',
  },
  {
    // New. A read: producing a report is not the same as keying one.
    path: '/ledger/trial-balance',
    componentName: 'LedgerTrialBalancePage',
    permission: PERMISSIONS.ledger.trialBalanceView,
    phoneBehaviour: 'reads',
  },
  {
    // New. The stored rollups behind the trial balance. Gated on the same code, because it is the
    // same figures at a finer grain.
    path: '/ledger/daily-balances',
    componentName: 'LedgerDailyBalancesPage',
    permission: PERMISSIONS.ledger.trialBalanceView,
    phoneBehaviour: 'reads',
  },
  {
    // New. Reading the periods is open to any ledger reader; closing and reopening carry their own
    // gates inside the screen, and reopening is countersigned.
    path: '/ledger/posting-periods',
    componentName: 'LedgerPostingPeriodsPage',
    permission: PERMISSIONS.ledger.periodClose,
    phoneBehaviour: 'reads',
  },
  {
    // New. Reading and resolving sit under one code, which is weaker than resolving deserves — see
    // the module's gap list. The server is the authority on the disposition.
    path: '/ledger/balance-drift',
    componentName: 'LedgerBalanceDriftPage',
    permission: PERMISSIONS.ledger.driftView,
    phoneBehaviour: 'reads',
  },
  {
    // New. Gated on an *operations* permission rather than a ledger one, which is a property of the
    // contract and is stated on the screen.
    path: '/ledger/audit',
    componentName: 'LedgerAuditPage',
    permission: PERMISSIONS.ops.auditView,
    phoneBehaviour: 'reads',
  },
]
